import useSWR from 'swr';
import { useContext, useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import styled from 'styled-components';
import RestaurantContext from '../../Context/RestaurantContext';
import LoadIndicatorComponent from '../Loader/LoadIndicatorComponent';
import ArticleComponent from './ArticleComponent';
import PickUpSwitchComponent from '../Cart/PickUpSwitchComponent';
import { Container } from 'react-bootstrap';
import StoreStatusComponent from './StoreStatusComponent';
import CategoryAndSearchBar from './CategoryAndSearchBar';
import Sticky from 'react-stickynode';
import { Waypoint } from 'react-waypoint';
import LoadingErrorReloadComponent from '../Loader/LoadingErrorReloadComponent';
import Fuse from 'fuse.js';
import cloneDeep from 'lodash/cloneDeep';
import {
  parseDayArrToString,
  parseSecondsToTime,
  isDtControlledAvailable,
} from '../../util/dayHelpers';
import NutritionInfoModal from './NutritionInfoModal';

const Wrapper = styled.div`
  display: flex;
  flex-flow: column;
`;

const SearchBarWrapper = styled.div`
  position: relative;
  height: 55px;
`;

const MobilePickUpSwitchWrapper = styled.div`
  @media (max-width: 991.98px) {
    display: block;
    visibility: visible;
  }

  display: none;
  visibility: hidden;
  padding-top: 20px;
`;

const MenuWrapper = styled.div`
  position: relative;
`;

const ProductGroupDtControlledP = styled.p`
  margin: 10px 0;
  text-align: center;
  font-style: italic;
  color: rgb(105, 105, 105);
  font-size: 14px;
  letter-spacing: 0.66px;
  font-weight: 300;
  padding: 0 15px;
`;
const ProductGroupDescriptionP = styled.p`
  margin: 10px 0;
  text-align: center;
  color: rgb(105, 105, 105);
  font-size: 14px;
  letter-spacing: 0.66px;
  font-weight: 400;
  padding: 0 15px;
`;

const ProductGroupImageWrapper = styled.div`
  position: relative;
  display: flex;
  overflow: hidden;

  height: 70px;
  @media (min-width: 576px) {
    height: 120px;
    border-radius: 4px;
  }
`;

const ProductGroupTitleWrapper = styled.div`
  display: flex;
  z-index: 2;
  padding: 0 15px;
  align-items: center;

  h2 {
    color: white;
    text-shadow: 0 3px 6px rgba(0, 0, 0, 0.5);
    font-weight: 300;

    font-size: 21px;
    letter-spacing: 0.9px;
    @media (min-width: 576px) {
      font-size: 28px;
      letter-spacing: 1.2px;
    }
  }
`;

const ArticleComponentWrapper = styled.div`
  display: block;
  width: 100%;

  &:not(:last-child) {
    margin-bottom: 5px;
  }
  &:last-child {
    margin-bottom: 10px;
  }
`;

const NoCategoryImagePlaceholder = styled.div`
  position: absolute;
  display: flex;
  width: 100%;
  height: 100%;
  background-color: #031e2b;
`;

const UstHint = styled.div`
  font-size: 10px;
  font-weight: 300;
  opacity: 0.8;
  text-align: center;
  padding-bottom: calc(10px + env(safe-area-inset-bottom) / 2);
`;

const NoSearchResults = styled.div`
  width: 100%;
  text-align: center;
  padding: 15px;

  h3 {
    font-size: 19px;
    font-weight: 700;
    margin-bottom: 0;
    color: #000000;
  }

  p {
    margin-bottom: 0px;
  }

  button {
    text-decoration: none;
  }
`;

/**
 * Native scrollTo with callback
 * @param offset - offset to scroll to
 * @param callback - callback function
 */
function scrollTo(offset, callback) {
  const reachedBottom = () =>
    Math.round(document.body.getBoundingClientRect().height) ===
    Math.round(window.pageYOffset) + Math.round(window.innerHeight);

  const goalIsInBottom = () =>
    offset >=
    Math.round(document.body.getBoundingClientRect().height) - Math.round(window.innerHeight);

  const onScroll = () => {
    // element reached latest when window scroll reached bottom
    if (reachedBottom() || Math.round(window.pageYOffset) === Math.round(offset)) {
      window.removeEventListener('scroll', onScroll);
      callback();
    }
  };

  // only scroll if there is possibility to scroll (-> window not already at bottom, and element in bottom scroll window or element not in Bottom area)
  if (!goalIsInBottom() || (goalIsInBottom() && !reachedBottom())) {
    window.addEventListener('scroll', onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: 'smooth',
    });
  }
}

const searchOptions = {
  caseSensitive: true,
  shouldSort: true,
  includeScore: true,
  threshold: 0.5,
  location: 0,
  distance: 100,
  maxPatternLength: 32,
  minMatchCharLength: 3,
  findAllMatches: true,
  keys: [
    {
      name: 'title',
      weight: 0.5,
    },
    {
      name: 'description',
      weight: 0.3,
    },
    {
      name: 'catTitle',
      weight: 0.2,
    },
  ],
};

const fetcher = (...args) =>
  fetch(...args).then((res) => {
    if (!res.ok) {
      const err = new Error('No 2xx response');
      err.response = res;
      throw err;
    }
    return res.json();
  });
function MenuComponent(props) {
  const { restaurantProps, resetMenuSearch } = useContext(RestaurantContext);
  const menuApiUrl = `/api/restaurants/v1/menuOfRestaurant?restaurantId=${restaurantProps.restaurantId}`;
  const { data, error, mutate } = useSWR(menuApiUrl, fetcher);

  const [focusedProductGroupId, setFocusedProductGroupId] = useState(undefined);
  const [fireWaypoints, setFireWaypoints] = useState(true);
  const [productGroupList, setProductGroupList] = useState();
  const [articleList, setArticleList] = useState([]);
  const [fuseList, setFuseList] = useState(new Fuse(articleList, searchOptions));
  const [filteredAndSearchProductGroupList, setFilteredAndSearchProductGroupList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const categoryRefs = useRef([]);

  useEffect(() => {
    if (!error && data && Array.isArray(data)) {
      // filter empty productGroups
      let filteredList = data.filter((x) => x.articles?.length > 0);
      // set currently available when dtControlled
      filteredList = filteredList.map((pg) => ({
        ...pg,
        currentlyAvailable: isDtControlledAvailable(pg),
      }));
      // sort array if its dtControlled available
      filteredList.sort((a, b) => {
        const a_Available = a.currentlyAvailable;
        const b_Available = b.currentlyAvailable;
        if (a_Available === b_Available) {
          return 0;
        } else if (a_Available && !b_Available) {
          return -1;
        } else if (!a_Available && b_Available) {
          return 1;
        }
      });

      setProductGroupList(filteredList);
    }
  }, [data, error]);

  // when we have a productGroupList, we extract all articles to filter by search term
  useEffect(() => {
    if (productGroupList?.length > 0) {
      setArticleList(
        productGroupList.flatMap((pg) =>
          pg.articles.map((article) => ({
            _id: article._id,
            title: article.title,
            description: article.description,
            catTitle: pg.title,
          })),
        ),
      );
    }
  }, [productGroupList]);

  useEffect(() => {
    if (articleList?.length > 0) {
      setFuseList(new Fuse(articleList, searchOptions));
    }
  }, [articleList]);

  useEffect(() => {
    if (productGroupList?.length > 0) {
      setFilteredAndSearchProductGroupList(filterCategoryList(productGroupList, searchTerm));
      setIsLoading(false);
    }
  }, [searchTerm, fuseList, productGroupList]);

  const filterCategoryList = (list, searchStr) => {
    if (!list) return [];
    let result = list;

    // filter by search input
    if (searchStr && searchStr.length > 3) {
      result = [];
      const filteredArticleList = fuseList.search(searchStr).flatMap((x) => x.item);
      // remove categories without matching articles
      list.forEach((productGroup) => {
        const clone = cloneDeep(productGroup);
        clone.articles = productGroup.articles.filter((article) =>
          filteredArticleList.some((fA) => fA._id === article._id),
        );
        result.push(clone);
      });
      // remove categories without result
      result = result.filter((pG) => pG.articles.length > 0);
    }
    return result;
  };

  const scrollToCatWithIndex = (catIndex) => {
    setFocusedProductGroupId(productGroupList[catIndex]._id);

    scrollWindowToCategory(catIndex);
  };

  const scrollWindowToCategory = (index) => {
    const offset = 150;
    const element = categoryRefs.current[index];
    const bounds = element.getBoundingClientRect();
    const y = bounds.top + window.pageYOffset - offset;
    setFireWaypoints(false);
    scrollTo(y, () => {
      setFireWaypoints(true);
    });
  };

  /* Nutrition Modal */
  const [nutritionModalData, setNutritionModalData] = useState({
    show: false,
  });
  const showNutritionModal = (data) => {
    setNutritionModalData({ show: true, data });
  };
  const hideNutritionModal = () => setNutritionModalData({ show: false });
  /* END Nutrition Modal */

  if (error) return <LoadingErrorReloadComponent reloadFn={() => mutate(menuApiUrl)} />;
  if ((!error && (!data || !productGroupList || productGroupList?.length === 0)) || isLoading)
    return (
      <LoadIndicatorComponent
        display
        style={{ marginTop: '150px' }}
        title="Einen kurzen Augenblick"
        subTitle="Speisekarte wird geladen..."
      />
    );

  return (
    <Wrapper>
      <StoreStatusComponent />

      <MobilePickUpSwitchWrapper>
        <Container>
          <PickUpSwitchComponent />
        </Container>
      </MobilePickUpSwitchWrapper>

      <Sticky top="#header" innerZ={3}>
        <SearchBarWrapper>
          <CategoryAndSearchBar
            categoryList={productGroupList}
            focusedProductGroupId={focusedProductGroupId}
            triggerScrollToCategoryIndex={scrollToCatWithIndex}
            onSearchTermChanged={setSearchTerm}
          />
        </SearchBarWrapper>
      </Sticky>

      <MenuWrapper>
        {filteredAndSearchProductGroupList?.length > 0 ? (
          <>
            {filteredAndSearchProductGroupList.map((productGroup, index) => (
              <Waypoint
                key={`menu-${productGroup._id}`}
                scrollableAncestor="window"
                topOffset="50%"
                bottomOffset="200px"
                fireOnRapidScroll={false}
                onEnter={(event) => {
                  if (event && event.event && fireWaypoints) {
                    setFocusedProductGroupId(productGroup._id);
                  }
                }}
              >
                <div
                  key={productGroup._id}
                  ref={(r) => {
                    categoryRefs.current[index] = r;
                  }}
                >
                  <ProductGroupImageWrapper>
                    {productGroup.image ? (
                      <Image
                        src={productGroup.image.cdnCategoryImagePath}
                        alt={productGroup.image.imageTitle}
                        fill
                        sizes="100vw"
                        style={{
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <NoCategoryImagePlaceholder>&nbsp;</NoCategoryImagePlaceholder>
                    )}
                    <ProductGroupTitleWrapper>
                      <h2>{productGroup.title}</h2>
                    </ProductGroupTitleWrapper>
                  </ProductGroupImageWrapper>
                  {productGroup.dtControlled && productGroup.activeDays?.length > 0 && (
                    <ProductGroupDtControlledP>
                      Artikel aus dieser Kategorie sind nur an folgenden Tagen bestellbar:{' '}
                      {parseDayArrToString(productGroup.activeDays)}{' '}
                      {productGroup.timeControlled && (
                        <span>
                          von {parseSecondsToTime(productGroup.timeFrom)} bis{' '}
                          {parseSecondsToTime(productGroup.timeUntil)}
                        </span>
                      )}
                    </ProductGroupDtControlledP>
                  )}
                  <ProductGroupDescriptionP>{productGroup.description}</ProductGroupDescriptionP>

                  {productGroup.articles.map((article) => (
                    <div key={article._id}>
                      <ArticleComponentWrapper>
                        <ArticleComponent
                          article={article}
                          isPGAvailable={productGroup.currentlyAvailable}
                          onNutritionClick={showNutritionModal}
                        />
                      </ArticleComponentWrapper>
                    </div>
                  ))}
                </div>
              </Waypoint>
            ))}
          </>
        ) : (
          <NoSearchResults>
            <h3>Keine Artikel gefunden</h3>
            <p>die deiner Suche entsprechen</p>

            <button
              className={'btn btn-link'}
              type="button"
              onClick={() => {
                resetMenuSearch();
              }}
            >
              Suche zurücksetzen
            </button>
          </NoSearchResults>
        )}
      </MenuWrapper>
      <UstHint>
        <span>Alle Preise inkl. gesetzl. USt.</span>
      </UstHint>

      <NutritionInfoModal
        show={nutritionModalData.show}
        onHide={hideNutritionModal}
        data={nutritionModalData.data}
      />
    </Wrapper>
  );
}

export default MenuComponent;
